import { useEffect, useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { useAuth } from "@/context/AuthContext";
import { Loader2, Sparkles, LogOut, Calendar, ArrowRight } from "lucide-react";

const Dashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, setUser, logout } = useAuth();
  const [isAuthenticated, setIsAuthenticated] = useState(
    location.state?.user ? true : null
  );
  const [quizzes, setQuizzes] = useState([]);
  const [loadingQuizzes, setLoadingQuizzes] = useState(true);

  useEffect(() => {
    if (location.state?.user) return;
    const checkAuth = async () => {
      try {
        const response = await axios.get(`${API}/auth/me`, {
          withCredentials: true,
        });
        setUser(response.data);
        setIsAuthenticated(true);
      } catch (error) {
        setIsAuthenticated(false);
        navigate("/", { replace: true });
      }
    };
    checkAuth();
  }, [location, navigate, setUser]);

  useEffect(() => {
    if (!isAuthenticated) return;
    const fetchQuizzes = async () => {
      try {
        const response = await axios.get(`${API}/auth/my-quizzes`, {
          withCredentials: true,
        });
        setQuizzes(response.data.quizzes);
      } catch (error) {
        console.error("Failed to load quizzes", error);
      } finally {
        setLoadingQuizzes(false);
      }
    };
    fetchQuizzes();
  }, [isAuthenticated]);

  if (isAuthenticated === null) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#C5A059] animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated) return null;

  return (
    <div
      className="min-h-screen bg-[#F9F8F6] pt-32 pb-20 px-6 md:px-12"
      data-testid="dashboard-page"
    >
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6 mb-12">
          <div className="flex items-center gap-4">
            {user?.picture && (
              <img
                src={user.picture}
                alt={user.name}
                className="w-16 h-16 rounded-full border-2 border-[#C5A059]"
                data-testid="dashboard-user-avatar"
              />
            )}
            <div>
              <p className="overline mb-1">Welcome back</p>
              <h1
                className="font-heading text-3xl md:text-4xl text-[#2C3524]"
                data-testid="dashboard-user-name"
              >
                {user?.name?.split(" ")[0] || "friend"}
              </h1>
            </div>
          </div>
          <button
            onClick={logout}
            className="flex items-center gap-2 text-[#4A5543] hover:text-[#C5A059] font-light text-sm self-start md:self-auto"
            data-testid="dashboard-signout-btn"
          >
            <LogOut className="w-4 h-4" />
            Sign out
          </button>
        </div>

        {/* Quick Cards */}
        <div className="grid md:grid-cols-3 gap-6 mb-12">
          <Link
            to="/quizzes"
            className="card group hover:border-[#C5A059] transition-all"
          >
            <Sparkles
              className="w-8 h-8 text-[#C5A059] mb-4"
              strokeWidth={1.5}
            />
            <h3 className="font-heading text-xl text-[#2C3524] mb-2">
              Take a Quiz
            </h3>
            <p className="text-[#4A5543] font-light text-sm">
              Reset Type, Peptide Readiness, or Find Your Retreat.
            </p>
          </Link>

          <Link
            to="/members"
            className="card group hover:border-[#C5A059] transition-all"
          >
            <Calendar
              className="w-8 h-8 text-[#C5A059] mb-4"
              strokeWidth={1.5}
            />
            <h3 className="font-heading text-xl text-[#2C3524] mb-2">
              Course Library
            </h3>
            <p className="text-[#4A5543] font-light text-sm">
              Six modules. Every framework I use with private clients.
            </p>
          </Link>

          <Link
            to="/#retreats"
            className="card group hover:border-[#C5A059] transition-all"
          >
            <ArrowRight
              className="w-8 h-8 text-[#C5A059] mb-4"
              strokeWidth={1.5}
            />
            <h3 className="font-heading text-xl text-[#2C3524] mb-2">
              Upcoming Retreats
            </h3>
            <p className="text-[#4A5543] font-light text-sm">
              Tulum, Bali, Brazil, Costa Rica — get on the list.
            </p>
          </Link>
        </div>

        {/* Quiz History */}
        <div className="card" data-testid="dashboard-quiz-history">
          <div className="flex items-center justify-between mb-6">
            <div>
              <span className="overline mb-1 block">Your Journey</span>
              <h2 className="font-heading text-2xl text-[#2C3524]">
                Quiz History
              </h2>
            </div>
          </div>

          {loadingQuizzes ? (
            <div className="py-8 text-center">
              <Loader2 className="w-6 h-6 text-[#C5A059] animate-spin mx-auto" />
            </div>
          ) : quizzes.length === 0 ? (
            <div className="py-8 text-center">
              <p className="text-[#4A5543] font-light mb-4">
                You haven't taken the quiz yet.
              </p>
              <Link to="/#quiz" className="btn-primary text-sm">
                Take Your First Quiz
              </Link>
            </div>
          ) : (
            <ul className="space-y-4">
              {quizzes.map((q, i) => (
                <li
                  key={i}
                  className="flex items-center justify-between p-4 rounded-xl border border-[#E3DAC9] hover:border-[#C5A059] transition-colors"
                  data-testid={`quiz-history-${i}`}
                >
                  <div>
                    <p className="font-heading text-lg text-[#2C3524] capitalize">
                      {q.result_type.replace(/_/g, " ")}
                    </p>
                    <p className="text-xs text-[#4A5543]/70 mt-1">
                      {new Date(q.created_at).toLocaleDateString("en-US", {
                        month: "long",
                        day: "numeric",
                        year: "numeric",
                      })}
                    </p>
                  </div>
                  <span className="text-xs tracking-[0.15em] uppercase text-[#C5A059]">
                    Result
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
